import type { ReactNode, SyntheticEvent } from 'react';
import { useRef } from 'react';

import { RestrictToElement } from '@dnd-kit/dom/modifiers';
import { useSortable } from '@dnd-kit/react/sortable';
import AddOutlined from '@mui/icons-material/AddOutlined';
import ClearOutlined from '@mui/icons-material/ClearOutlined';
import { Box, Tab, Tabs as MuiTabs } from '@mui/material';
import { usePostHog } from 'posthog-js/react';

import { usePageInformationContext } from '@/context/PageInformationContext';

import { TooltipIconButton } from '../TooltipIconButton';

interface Tab {
  name: string;
  id?: string;
  disabled?: boolean;
  label?: ReactNode;
  closable?: boolean;
  onClose?: (value: number) => void;
  tag?: ReactNode;
  sortableHandleRef?: React.RefObject<HTMLElement | null>;
}

interface CustomTabsProps {
  tabs: Tab[];
  activeTab: number;
  onChange?: (index: number) => void;
  onAddTab?: () => void;
  height?: number;
  minTabWidth?: number;
  offset?: number;
  maxTabWidth?: number | 'unset';
  scrollButtons?: boolean | 'auto';
  disableRipple?: boolean;
}

type MuiTabsInjectedTabProps = {
  fullWidth?: boolean;
  indicator?: ReactNode;
  onChange?: (event: SyntheticEvent, value: unknown) => void;
  selected?: boolean;
  selectionFollowsFocus?: boolean;
  textColor?: 'inherit' | 'primary' | 'secondary';
  value?: unknown;
};

interface SortableTabItemProps extends MuiTabsInjectedTabProps {
  id: string;
  index: number;
  handle?: React.RefObject<HTMLElement | null>;
  container: React.RefObject<HTMLDivElement | null>;
  label: ReactNode;
  height: number;
  minTabWidth: number;
  maxTabWidth: number | 'unset';
  disableFocusRipple?: boolean;
  disableRipple?: boolean;
  disabled?: boolean;
  ariaControls: string;
  tabId: string;
  name: string;
  onSelectTab?: (index: number) => void;
  activeTab: number;
  onClick: () => void;
}

const SortableTabItem = ({
  id,
  index,
  handle,
  container,
  label,
  height,
  minTabWidth,
  maxTabWidth,
  disableFocusRipple,
  disableRipple,
  disabled,
  ariaControls,
  tabId,
  name,
  onSelectTab,
  activeTab,
  onClick,
  ...muiTabProps
}: SortableTabItemProps) => {
  const internalMuiTabProps = muiTabProps as React.ComponentProps<typeof Tab>;

  const { ref } = useSortable({
    id,
    index,
    handle,
    type: 'dashboard-tab',
    accept: ['dashboard-tab'],
    modifiers: [RestrictToElement.configure({ element: container.current })]
  });

  return (
    <Tab
      ref={ref}
      {...internalMuiTabProps}
      aria-controls={ariaControls}
      disabled={disabled}
      disableFocusRipple={disableFocusRipple}
      disableRipple={disableRipple}
      id={tabId}
      label={
        <Box
          role='presentation'
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            width: '100%',
            height: '100%',
            textAlign: 'center'
          }}
          onMouseDownCapture={event => {
            if (onSelectTab && activeTab !== index) {
              event.stopPropagation();
              onSelectTab(index);
            }
          }}
        >
          <Box sx={{ flexBasis: '100%' }}>{label}</Box>
        </Box>
      }
      sx={{
        alignItems: 'flex-start',
        textTransform: 'initial',
        height,
        minWidth: `${minTabWidth}px`,
        maxWidth: `${maxTabWidth}px`,
        minHeight: `${height}px`,
        p: 1,
        '&:not(:last-child)': { mr: '2px' }
      }}
      onClick={onClick}
    />
  );
};

const CloseButton = ({
  label,
  name,
  onClose,
  index
}: {
  label: ReactNode | undefined;
  index: number;
  name: string;
  onClose?: (value: number) => void;
}) => {
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
        {label || name}
      </Box>
      <TooltipIconButton
        component='span'
        sx={{
          height: 46,
          position: 'absolute',
          right: 0,
          backgroundColor: 'transparent',
          ':hover': {
            backgroundColor: 'action.hover'
          }
        }}
        title='Remove'
        onClick={e => {
          e.stopPropagation();
          if (onClose) onClose(index);
        }}
      >
        <ClearOutlined />
      </TooltipIconButton>
    </>
  );
};

const CustomTabs = ({
  tabs,
  activeTab,
  onChange,
  onAddTab,
  height = 48,
  minTabWidth = 180,
  maxTabWidth = 'unset',
  offset = 0,
  scrollButtons = false,
  disableRipple = false,
  ...tabProps
}: CustomTabsProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    if (onChange) onChange(newValue);
  };

  const { mainPageTitle, subPageTitle } = usePageInformationContext();

  const postHog = usePostHog();
  const handleTabClick = (
    mainPage: string | undefined,
    subPage: string | undefined,
    tabName: string
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- postHog can be null when not configured
    postHog?.capture('tab_click', {
      mainPage,
      subPage,
      tabName
    });
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
      <MuiTabs
        ref={containerRef}
        aria-label='tabs'
        scrollButtons={scrollButtons}
        sx={{
          minWidth: 0,
          maxWidth: `calc(100% - ${offset}px)`,
          minHeight: `${height}px`,
          height,
          '& .MuiTabs-scrollButtons.Mui-disabled': {
            display: 'none'
          },
          '.MuiTabScrollButton-root': {
            width: 32,
            minWidth: 32,
            backgroundColor: 'gx.tileLight',
            color: 'gx.textDisabled',
            '&:hover': {
              color: 'white',
              backgroundColor: '#161b40'
            }
          }
        }}
        value={activeTab}
        variant='scrollable'
        onChange={handleChange}
      >
        {tabs.map(
          ({ name, id, label, disabled, closable, onClose, tag, sortableHandleRef }, index) => {
            const tabLabel = closable ? (
              <CloseButton
                index={index}
                label={label}
                name={name}
                onClose={onClose}
              />
            ) : (
              <Box sx={{ flexBasis: '100%' }}>{label || name}</Box>
            );

            if (id && sortableHandleRef) {
              return (
                <SortableTabItem
                  key={id}
                  activeTab={activeTab}
                  ariaControls={`tabpanel-${name}`}
                  container={containerRef}
                  disableFocusRipple={disableRipple}
                  disableRipple={disableRipple}
                  disabled={disabled}
                  handle={sortableHandleRef}
                  height={height}
                  id={id}
                  index={index}
                  label={
                    <>
                      {tabLabel}
                      {tag}
                    </>
                  }
                  maxTabWidth={maxTabWidth}
                  minTabWidth={minTabWidth}
                  name={name}
                  tabId={`tab-${name}`}
                  onClick={() => {
                    handleTabClick(mainPageTitle, subPageTitle, name);
                  }}
                  onSelectTab={onChange}
                />
              );
            }

            return (
              <Tab
                key={id ?? `tab-${name}-${index}`}
                aria-controls={`tabpanel-${name}`}
                disabled={disabled}
                disableFocusRipple={disableRipple}
                disableRipple={disableRipple}
                id={`tab-${name}`}
                label={
                  <Box
                    role='presentation'
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      width: '100%',
                      height: '100%',
                      textAlign: 'center',
                      cursor: 'pointer',
                      borderRadius: '4px'
                    }}
                    onMouseDownCapture={event => {
                      if (onChange && activeTab !== index) {
                        event.stopPropagation();
                        onChange(index);
                      }
                    }}
                  >
                    {tabLabel}
                    {tag}
                  </Box>
                }
                sx={{
                  alignItems: 'flex-start',
                  textTransform: 'initial',
                  height,
                  minWidth: `${minTabWidth}px`,
                  maxWidth: `${maxTabWidth}px`,
                  minHeight: `${height}px`,
                  p: 1,
                  '&:not(:last-child)': { mr: '2px' }
                }}
                onClick={() => {
                  handleTabClick(mainPageTitle, subPageTitle, name);
                }}
                {...tabProps}
              />
            );
          }
        )}
      </MuiTabs>
      {onAddTab && (
        <TooltipIconButton
          sx={{ width: 32, minWidth: 32, backgroundColor: 'gx.tileLight' }}
          title='Add new page'
          onClick={onAddTab}
        >
          <AddOutlined sx={{ fontSize: '14px !important', color: 'gx.textDisabled' }} />
        </TooltipIconButton>
      )}
    </Box>
  );
};

export type { Tab };
export default CustomTabs;
